import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { copyObject } from 'common/copyObject';
import { useStore } from 'store/StoreContext';
import { useNext } from 'hooks/UseNext3_2To4';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/UserRegister3Validator2';
import { Country } from 'consts/CountryConst';
import { Prefecture } from 'consts/PrefectureConst';
import { useSiteDisplayItems, SiteDisplayItemsExtend } from 'hooks/UseSiteDisplayItems';
import { useSearchAddress } from 'hooks/UseSearchAddress';
import { SelectAddressDialog } from 'components/organisms/SelectAddressDialog';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { convertHalfToFull, convertFullToHalf } from 'common/CharacterConverter';

type Props = {};

export const UserRegister3Template2 = memo((_props: Props) => {
	const history = useHistory<SiteDisplayItemsExtend>();
	const [store, dispatch] = useStore();
	const { searchAddress, zipResponse, zipResponses, isDialogOpen, doSelect, doClose } = useSearchAddress();
	const { displayItems, isRequired, isOptional, isNone, getSiteDisplayItemLoading } = useSiteDisplayItems(
		store,
		history.location.state,
	);
	const [formData, setFormData] = useState(copyObject(store.registerUserData));
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [processLoading, setProcessLoading] = useState(false);
	const { next } = useNext(
		formErrors,
		setFormErrors,
		getErrorMessage,
		formData,
		dispatch,
		displayItems,
		setProcessLoading,
	);
	const back = () => {
		history.push(RoutePath.USER_REGISTER_3_1, displayItems);
	};

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleChangeSelectValue = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const [isPlaceHolder, setIsPlaceHolder] = useState(!formData.pref_cd);
	const handleChangeSelectValueWithPlaceHolder = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setIsPlaceHolder(!event.target.value);
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleBlurConvertHalfToFullValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertHalfToFull(event.target.value) }));
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};

	// 住所検索関連
	const search = async () => {
		await searchAddress(formData.zip);
	};
	useEffect(() => {
		if (zipResponse) {
			// 入力欄に格納
			setFormData((prev) => ({
				...prev,
				zip: zipResponse.zipcode,
				pref_cd: isNone(displayItems.pref_cd) ? prev.pref_cd : zipResponse.prefcode,
				address_2: isNone(displayItems.address_2) ? prev.address_2 : zipResponse.address2,
				address_3: isNone(displayItems.address_3) ? prev.address_3 : zipResponse.address3,
			}));
			setIsPlaceHolder(!zipResponse.prefcode);
		}
	}, [zipResponse]);

	const scrollRef = useRef(null);
	useEffect(() => {
		if (!store.registerUserData.mailaddress_1) {
			console.error(`error: mailaddress not exists.`);
			history.push(RoutePath.ERROR);
		}
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜本会員登録 追加項目入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors]);

	return (
		<main>
			<Backdrop className={classes.backdrop} open={getSiteDisplayItemLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-03.svg" alt="本会員登録" />
						<img className="sp" src="images/img-steps-03_sp.svg" alt="" />
					</div>
					<div className="step-title sp">本会員登録</div>
					<p>
						下記登録フォームに必要事項を入力し、「確認画面へ」ボタンを押してください。
						<br />
						本会員登録内容確認画面に移動します。
					</p>

					{isFormError ? (
						<div className="error-box" ref={errorRef}>
							<>
								<p>※下記項目は全て必須となります。</p>
								<div className="error-box-in">
									{formErrors.tel !== '' ? <p>【電話番号】{formErrors.tel}</p> : null}
									{formErrors.zip !== '' ? <p>【郵便番号】{formErrors.zip}</p> : null}
									{formErrors.pref_cd !== '' ? <p>【都道府県】{formErrors.pref_cd}</p> : null}
									{formErrors.address_2 !== '' ? <p>【市区町村】{formErrors.address_2}</p> : null}
									{formErrors.address_3 !== '' ? <p>【町名・番地】{formErrors.address_3}</p> : null}
									{formErrors.address_4 !== '' ? <p>【マンション名】{formErrors.address_4}</p> : null}
									{formErrors.nickname !== '' ? <p>【ニックネーム】{formErrors.nickname}</p> : null}
									{formErrors.country !== '' ? <p>【国】{formErrors.country}</p> : null}
								</div>
							</>
						</div>
					) : null}

					{isNone(displayItems.tel) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="tel">電話番号</label>
								{isRequired(displayItems.tel) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.tel) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※半角数字</span>
							</div>
							<div className="form-input width50">
								<input
									id="tel"
									type="tel"
									name="tel"
									placeholder="12345678910"
									value={formData.tel}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
							</div>
							<p className="error">{formErrors.tel}</p>
						</div>
					)}

					{isNone(displayItems.zip) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="zip">郵便番号</label>
								{isRequired(displayItems.zip) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.zip) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※半角数字 ハイフンなし</span>
							</div>
							<div className="form-input width50">
								<input
									id="zip"
									type="text"
									name="zip"
									placeholder="1234567"
									value={formData.zip}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
								{isNone(displayItems.pref_cd) &&
								isNone(displayItems.address_2) &&
								isNone(displayItems.address_3) ? null : (
									<div className="address-search-btn">
										<button onClick={search}>住所検索</button>
									</div>
								)}
							</div>
							<p className="error">{formErrors.zip}</p>
						</div>
					)}

					{isNone(displayItems.pref_cd) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="pref_cd">都道府県</label>
								{isRequired(displayItems.pref_cd) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.pref_cd) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="pref_cd"
										name="pref_cd"
										className={isPlaceHolder ? classes.placeholderOption : ''}
										value={formData.pref_cd}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											都道府県を選択
										</option>
										{Object.values(Prefecture).map((v) => {
											return (
												<option value={v.key} className={classes.defaultOption}>
													{v.name}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.pref_cd}</p>
						</div>
					)}

					{isNone(displayItems.address_2) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_2">市区町村</label>
								{isRequired(displayItems.address_2) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_2) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<input
									id="address_2"
									type="text"
									name="address_2"
									placeholder="品川区"
									value={formData.address_2}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_2}</p>
						</div>
					)}

					{isNone(displayItems.address_3) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_3">町名・番地</label>
								{isRequired(displayItems.address_3) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_3) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="address_3"
									type="text"
									name="address_3"
									placeholder="西五反田２−１１−８"
									value={formData.address_3}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_3}</p>
						</div>
					)}

					{isNone(displayItems.address_4) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_4">マンション名</label>
								{isRequired(displayItems.address_4) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_4) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="address_4"
									type="text"
									name="address_4"
									placeholder="○○マンション１０１号室"
									value={formData.address_4}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_4}</p>
						</div>
					)}

					{isNone(displayItems.nickname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="nickname">ニックネーム</label>
								{isRequired(displayItems.nickname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.nickname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="nickname"
									type="text"
									name="nickname"
									placeholder="がっけんたろう"
									value={formData.nickname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.nickname}</p>
						</div>
					)}

					{isNone(displayItems.country) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="country">国</label>
								{isRequired(displayItems.country) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.country) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select id="country" name="country" value={formData.country} onChange={handleChangeSelectValue}>
										{Object.values(Country).map((v) => {
											return <option value={v.key}>{v.name}</option>;
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.country}</p>
						</div>
					)}

					<div className="btn-box">
						{processLoading ? (
							<CircularProgress />
						) : (
							<>
								<div className="btn wh">
									<button onClick={back}>戻る</button>
								</div>
								<div className="btn gr">
									<button onClick={next}>確認画面へ</button>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
			{/* 住所選択ダイアログ */}
			<SelectAddressDialog isOpen={isDialogOpen} data={zipResponses} doSelect={doSelect} doClose={doClose} />
		</main>
	);
});
